import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Coins,
  Banknote,
  Gift,
  LayoutDashboard,
  Lock,
  LogOut,
  Menu,
  RefreshCw,
  Settings,
  Users,
  Wallet as WalletIcon,
  X,
  ArrowLeftRight,
  AlertTriangle,
  FlaskConical,
  ReceiptText,
} from 'lucide-react';
import { gauntlet, type SyncResult } from '../lib/api';
import { CommandPalette } from './CommandPalette';
import { useSession } from '../store/session';
import { cx } from '../lib/format';
import { Button } from './ui';

const NAV = [
  { to: '/', label: 'Dashboard', Icon: LayoutDashboard, end: true },
  { to: '/activity', label: 'Activity', Icon: ArrowLeftRight, end: false },
  { to: '/family', label: 'Family', Icon: Users, end: false },
  { to: '/add-funds', label: 'Add funds', Icon: Banknote, end: false },
  { to: '/requests', label: 'Requests', Icon: ReceiptText, end: false },
  { to: '/spend', label: 'Spend', Icon: Gift, end: false },
  { to: '/chains', label: 'Chains', Icon: Coins, end: false },
  { to: '/testing', label: 'Try it free', Icon: FlaskConical, end: false, adminOnly: true },
  { to: '/settings', label: 'Settings', Icon: Settings, end: false },
];

export function Layout({ children }: { children: ReactNode }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const isAdmin = useSession((s) => s.isAdmin);
  const [syncing, setSyncing] = useState(false);
  const [syncReport, setSyncReport] = useState<SyncResult | null>(null);
  const username = useSession((s) => s.username);
  const signOut = useSession((s) => s.signOut);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: walletData } = useQuery({
    queryKey: ['wallets'],
    queryFn: () => gauntlet.wallets(),
    refetchInterval: 30_000,
  });

  const wallets = walletData?.wallets ?? [];
  const anyUnlocked = wallets.some((w) => w.unlocked);

  // Close the mobile drawer whenever the route changes.
  useEffect(() => {
    setMenuOpen(false);
  }, [children]);

  const runSync = async (): Promise<void> => {
    setSyncing(true);
    try {
      const result = await gauntlet.sync();
      // The result was previously discarded, errors and all — so a chain that
      // could not be reached looked identical to one holding nothing. "Why
      // doesn't this chain work?" had no answer anywhere in the UI.
      if (!result.skipped) setSyncReport(result);
      await queryClient.invalidateQueries();
    } catch (error) {
      setSyncReport({
        addressesChecked: 0,
        balancesUpdated: 0,
        utxosFound: 0,
        transfersFound: 0,
        errors: [
          {
            chainId: 'all',
            message: error instanceof Error ? error.message : 'Sync failed.',
          },
        ],
        durationMs: 0,
      });
    } finally {
      setSyncing(false);
    }
  };

  const lockEverything = async (): Promise<void> => {
    await Promise.all(wallets.filter((w) => w.unlocked).map((w) => gauntlet.lockWallet(w.id)));
    await queryClient.invalidateQueries({ queryKey: ['wallets'] });
  };

  return (
    <div className="flex min-h-dvh bg-void text-ink">
      <CommandPalette />
      {syncReport && (syncReport.errors.length > 0 || syncReport.transfersFound > 0) && (
        <div className="fixed bottom-4 right-4 z-40 w-full max-w-sm">
          <div
            className={cx(
              'rounded-xl border p-3 shadow-2xl backdrop-blur',
              syncReport.errors.length > 0
                ? 'border-warn/40 bg-warn/10'
                : 'border-gain/40 bg-gain/10',
            )}
          >
            <div className="flex items-start gap-2">
              {syncReport.errors.length > 0 ? (
                <AlertTriangle size={15} className="mt-0.5 shrink-0 text-warn" />
              ) : (
                <RefreshCw size={15} className="mt-0.5 shrink-0 text-gain" />
              )}
              <div className="min-w-0 flex-1 text-[13px]">
                <p className="font-medium">
                  Synced {syncReport.addressesChecked} address
                  {syncReport.addressesChecked === 1 ? '' : 'es'}
                  {syncReport.transfersFound > 0 &&
                    ` · ${syncReport.transfersFound} new transfer${syncReport.transfersFound === 1 ? '' : 's'}`}
                </p>
                {syncReport.errors.length > 0 && (
                  <>
                    <p className="mt-1 text-xs text-ink-dim">
                      {syncReport.errors.length} chain
                      {syncReport.errors.length === 1 ? '' : 's'} could not be reached. This is
                      almost always the public indexer being down or rate-limiting — not your
                      wallet. Point that chain at your own node in Settings → Network.
                    </p>
                    <ul className="mt-1.5 max-h-32 space-y-0.5 overflow-y-auto">
                      {syncReport.errors.slice(0, 6).map((failure, i) => (
                        <li
                          key={i}
                          className="truncate text-[11px] text-ink-faint"
                          title={failure.message}
                        >
                          <span className="text-ink-dim">{failure.chainId}</span> — {failure.message}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
              <button
                onClick={() => setSyncReport(null)}
                className="shrink-0 rounded p-0.5 text-ink-faint hover:text-ink"
                aria-label="Dismiss"
              >
                <X size={14} />
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Sidebar */}
      <aside
        className={cx(
          'fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r border-hairline bg-abyss',
          'transition-transform duration-200 lg:translate-x-0',
          menuOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex h-16 items-center gap-2.5 border-b border-hairline px-5">
          <GauntletMark />
          <div className="min-w-0">
            <p className="text-sm font-semibold tracking-tight">Gauntlet</p>
            <p className="truncate text-[11px] text-ink-faint">self-hosted · non-custodial</p>
          </div>
        </div>

        <nav className="flex-1 space-y-0.5 overflow-y-auto p-3">
          {NAV.filter((n) => !('adminOnly' in n && n.adminOnly) || isAdmin).map(({ to, label, Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                cx(
                  'flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
                  isActive
                    ? 'bg-brass-wash text-brass'
                    : 'text-ink-dim hover:bg-raised hover:text-ink',
                )
              }
            >
              <Icon size={16} />
              {label}
            </NavLink>
          ))}

          {wallets.length > 0 && (
            <div className="pt-5">
              <p className="label-xs px-3 pb-2">Wallets</p>
              {wallets.map((wallet) => (
                <NavLink
                  key={wallet.id}
                  to={`/wallets/${wallet.id}`}
                  className={({ isActive }) =>
                    cx(
                      'flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
                      isActive
                        ? 'bg-raised text-ink'
                        : 'text-ink-dim hover:bg-raised hover:text-ink',
                    )
                  }
                >
                  <WalletIcon size={16} />
                  <span className="min-w-0 flex-1 truncate">{wallet.name}</span>
                  {/* An explicit lock glyph, because "can this spend right now?"
                      is the single most important piece of state in the app. */}
                  <span
                    className={cx(
                      'size-1.5 shrink-0 rounded-full',
                      wallet.unlocked ? 'bg-gain' : 'bg-ink-faint',
                    )}
                    title={wallet.unlocked ? 'Unlocked' : 'Locked'}
                  />
                </NavLink>
              ))}
            </div>
          )}
        </nav>

        <div className="space-y-2 border-t border-hairline p-3">
          {anyUnlocked && (
            <Button variant="secondary" size="sm" full icon={<Lock size={14} />} onClick={lockEverything}>
              Lock all wallets
            </Button>
          )}
          <div className="flex items-center justify-between px-1 pt-1">
            <span className="truncate text-xs text-ink-faint">{username}</span>
            <button
              onClick={async () => {
                await signOut();
                navigate('/login');
              }}
              className="rounded p-1 text-ink-faint transition-colors hover:text-loss"
              aria-label="Sign out"
            >
              <LogOut size={14} />
            </button>
          </div>
        </div>
      </aside>

      {menuOpen && (
        <div
          className="fixed inset-0 z-30 bg-void/70 lg:hidden"
          onClick={() => setMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Main */}
      <div className="flex min-w-0 flex-1 flex-col lg:pl-64">
        <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b border-hairline bg-void/85 px-4 backdrop-blur-md sm:px-6">
          <button
            className="rounded-lg p-2 text-ink-dim hover:bg-raised lg:hidden"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Toggle navigation"
          >
            {menuOpen ? <X size={18} /> : <Menu size={18} />}
          </button>

          <div className="flex-1" />

          <Button
            variant="secondary"
            size="sm"
            onClick={runSync}
            loading={syncing}
            icon={!syncing ? <RefreshCw size={14} /> : undefined}
          >
            {syncing ? 'Syncing…' : 'Sync'}
          </Button>
        </header>

        <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-6 sm:px-6 sm:py-8">{children}</main>

        <footer className="border-t border-hairline px-6 py-4">
          <p className="text-center text-[11px] leading-relaxed text-ink-faint">
            Gauntlet is self-custodial software. Your recovery phrase is the only way to restore your
            funds — nobody, including this server, can recover it for you.
          </p>
        </footer>
      </div>
    </div>
  );
}

/** The mark: a stylised gauntlet plate, drawn rather than shipped as an asset. */
function GauntletMark() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4 7.5 12 3l8 4.5v6.2c0 4.1-3.3 6.9-8 8.3-4.7-1.4-8-4.2-8-8.3V7.5Z"
        stroke="var(--color-brass)"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path d="M8.5 10.5h7M8.5 13.5h7M10 16.5h4" stroke="var(--color-brass)" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}
